import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, CircularProgress } from '@mui/material'
import { FeeRate, Fund, MgmtFeeRun, SuccessFeeRun } from '../api/types'
import { useOpsApi } from '../api/context'
import { apiError } from '../api/errors'
import { useCurrentUser } from '../auth'
import { PageHeader } from '../components/PageHeader'
import { SegmentedTabs } from '../components/SegmentedTabs'
import { MgmtFeeSection } from './fee/MgmtFeeSection'
import { SuccessFeeSection } from './fee/SuccessFeeSection'

type FeeTab = 'mgmt' | 'success'

/**
 * Fáze 6 — Fee. Management fee (měsíční fakturace per distributor, F6-B) + success
 * fee (roční stržení jednotek držitelům, F6-C). Fázi vidí všichni Apex; spouštět a
 * potvrzovat smí backoffice i CEO (F6-D), ostatní mají náhled read-only.
 */
export function FeePhase() {
  const api = useOpsApi()
  const user = useCurrentUser()
  const canManage = user.isBackoffice || user.isCeo

  const [tab, setTab] = useState<FeeTab>('mgmt')
  const [funds, setFunds] = useState<Fund[]>([])
  const [feeRates, setFeeRates] = useState<FeeRate[]>([])
  const [mgmtFeeRuns, setMgmtFeeRuns] = useState<MgmtFeeRun[]>([])
  const [successFeeRuns, setSuccessFeeRuns] = useState<SuccessFeeRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [f, r, m, s] = await Promise.all([
        api.listFunds(),
        api.listFeeRates(),
        api.listMgmtFeeRuns(),
        api.listSuccessFeeRuns(),
      ])
      setFunds(f)
      setFeeRates(r)
      setMgmtFeeRuns(m)
      setSuccessFeeRuns(s)
    } catch (err) {
      setError(apiError(err, 'Načtení fee dat selhalo'))
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    reload()
  }, [reload])

  return (
    <Box sx={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Fee"
        subtitle="Provize distributorů z management fee (kvartálně) + success fee (roční stržení jednotek)."
      />

      <SegmentedTabs
        items={[
          { value: 'mgmt', label: 'Management fee' },
          { value: 'success', label: 'Success fee' },
        ]}
        value={tab}
        onChange={(value) => setTab(value as FeeTab)}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tab === 'mgmt' ? (
        <MgmtFeeSection
          feeRates={feeRates}
          funds={funds}
          mgmtFeeRuns={mgmtFeeRuns}
          canManage={canManage}
          onChanged={reload}
        />
      ) : (
        <SuccessFeeSection
          funds={funds}
          successFeeRuns={successFeeRuns}
          canManage={canManage}
          onChanged={reload}
        />
      )}
    </Box>
  )
}
